import React, {useEffect, useRef, useState} from "react";
import {FlowData, RegisterFunction, SELFCARE_REPORT_FLOW_TYPE} from "../../../../util/types.ts";
import Icon from "../../Icon.tsx";
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import IconOpenInNew from "../../../icons/IconOpenInNew.tsx";
import {ConnectionTypeValues} from "../../../../util/SelfcareErrorReportTypes.ts";
import {scrollToElement} from "../../../../util/helpers.ts";

interface ConnectionTypeComponentProps {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    flowtype: SELFCARE_REPORT_FLOW_TYPE;
    register: RegisterFunction;
}

export default function ConnectionTypeComponent({
        flowData,
        setFlowData,
        flowtype,
        register,
    }: ConnectionTypeComponentProps) {

    const [connectionTypeError, setConnectionTypeError] = useState<string>('');
    const connectionTypeSectionComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFlowData((prevFlowData) => ({
            ...prevFlowData,
            connectionType: value,
        }));
    };

    const hasError = connectionTypeError.length > 0;

    useEffect((): void => {
        register("ConnectionTypeComponent",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    setConnectionTypeError('');
                    if (!flowData.connectionType) {
                        setConnectionTypeError("Vælg om du oplever problemer med wifi eller et netværkskabel");
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    scrollToElement(connectionTypeSectionComponentTop.current)
                },
            },
        )
    }, [register, connectionTypeError]);

    return (
        <div className={`input-container${hasError ? ' error' : ''}`} ref={connectionTypeSectionComponentTop}>
            <ScrollToAnchorPoint id={'connection-type-section'} className={'id-anchor--selfcare-report-error'}/>
            <p>Oplever du problemer på wifi, eller når du bruger internettet med et netværkskabel?*</p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'wifi_radio'}
                    name={'connection_type'}
                    value={ConnectionTypeValues.wifi}
                    checked={flowData.connectionType === ConnectionTypeValues.wifi}
                    onChange={handleRadioChange}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'wifi_radio'}
                >
                    Wifi
                </label>
            </p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'lan_radio'}
                    name={'connection_type'}
                    value={ConnectionTypeValues.cable}
                    checked={flowData.connectionType === ConnectionTypeValues.cable}
                    onChange={handleRadioChange}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'lan_radio'}
                >
                    Netværkskabel
                </label>
            </p>
            <p className={'with-checkbox'}>
                <input
                    type={'radio'}
                    id={'both_radio'}
                    name={'connection_type'}
                    value={'begge'}
                    checked={flowData.connectionType === 'begge'}
                    onChange={handleRadioChange}
                />
                <label
                    className={'checkbox-label checkbox-label--montserrat'}
                    htmlFor={'both_radio'}
                >
                    Begge
                </label>
            </p>

            {flowData.connectionType === ConnectionTypeValues.wifi && flowtype === SELFCARE_REPORT_FLOW_TYPE.SPEED && (
                <div className={'text-box'}>
                    <p>
                        Hastigheden på dit wifi bliver påvirket af mange faktorer i din bolig.
                        Vi anbefaler, at du læser nedenstående hjælpeartikel om, hvad du kan forvente af dit wifi:
                    </p>
                    <p className={'align-center'}>
                        <a
                            className={'button button--montserrat teal rounded with-icon'}
                            href={'/hjaelp/internet-og-wifi/forventninger-til-wifi'}
                            target={'_blank'}
                        >
                            <span className={'text'}>
                                Læs om hvad du kan forvente af dit wifi
                            </span>
                            <Icon component={IconOpenInNew}/>
                        </a>
                    </p>
                </div>
            )}
            {(flowData.connectionType === ConnectionTypeValues.cable || flowData.connectionType === ConnectionTypeValues.both) && flowtype === SELFCARE_REPORT_FLOW_TYPE.SPEED && (
                <div className={'text-box'}>
                    <p>
                        Vi anbefaler, at du laver en hastighedstest med nedenstående guide, før du fejlmelder.
                        Så er du sikker på, at få det mest nøjagtige resultat.
                    </p>
                    <p className={'align-center'}>
                        <a
                            className={'button button--montserrat teal rounded with-icon'}
                            href={'/hjaelp/internet-og-wifi/hastighedstest'}
                            target={'_blank'}
                        >
                            <span className={'text'}>
                                Læs hvordan du laver en hastighedstest
                            </span>
                            <Icon component={IconOpenInNew}/>
                        </a>
                    </p>
                </div>
            )}

            {hasError && <p className={'error-message'}>{connectionTypeError}</p>}
        </div>
    );
}
